import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useRef } from "react";
import {
    ContourLines,
    SurveyingScene,
    CompassRose,
    FloatingAnnotations,
    SubdivisionGrid,
    CoordinateGrid,
    PropertyBoundary,
    Monument,
    SurveyPoint,
} from "@/components/decorative/SurveyingElements";

/**
 * PageHero — reusable animated hero wrapper with page-specific surveying decorations.
 * Each variant uses DIFFERENT surveying vectors so pages feel distinct.
 *
 * variants:
 *  - home         → contour + scene + compass + subdivision + annotations
 *  - services     → property boundary polygon + compass
 *  - about        → subdivision grid + monument + contour
 *  - contact      → coordinate grid + benchmark monument + survey points
 *  - get-started  → contour topo + compass + annotations
 *  - blog         → coordinate grid + floating annotations
 *  - service-detail → contour + compass (lighter decoration)
 *
 * tint: 'cyan' | 'violet' | 'amber' | 'emerald' | 'rose' (applies multicolor section bg)
 */
export default function PageHero({
    children,
    variant = "home",
    tint = "cyan",
    className = "",
    minHeight = "min-h-[420px]",
}) {
    const ref = useRef(null);
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const sx = useSpring(mouseX, { stiffness: 60, damping: 25 });
    const sy = useSpring(mouseY, { stiffness: 60, damping: 25 });
    const pX = useTransform(sx, [-1, 1], [-18, 18]);
    const pY = useTransform(sy, [-1, 1], [-12, 12]);
    const pX2 = useTransform(sx, [-1, 1], [30, -30]);
    const pY2 = useTransform(sy, [-1, 1], [22, -22]);

    const onMouseMove = (e) => {
        if (!ref.current) return;
        const r = ref.current.getBoundingClientRect();
        mouseX.set(((e.clientX - r.left) / r.width) * 2 - 1);
        mouseY.set(((e.clientY - r.top) / r.height) * 2 - 1);
    };

    return (
        <section
            ref={ref}
            onMouseMove={onMouseMove}
            className={`relative overflow-hidden bg-section-${tint} ${minHeight} ${className}`}
            data-testid={`page-hero-${variant}`}
        >
            {/* Surveyor grid backing */}
            <div className="absolute inset-0 survey-grid opacity-30" />

            {/* Variant-specific decorations */}
            {variant === "services" && (
                <>
                    <motion.div
                        style={{ x: pX2, y: pY2 }}
                        className="absolute inset-0 pointer-events-none"
                    >
                        <PropertyBoundary className="absolute inset-0 w-full h-full" opacity={0.3} />
                    </motion.div>
                    <motion.div
                        style={{ x: pX, y: pY }}
                        className="absolute -top-10 -right-10 w-[420px] h-[420px] pointer-events-none"
                    >
                        <ContourLines className="w-full h-full" opacity={0.28} count={7} />
                    </motion.div>
                    <div className="absolute top-10 right-8 hidden lg:block pointer-events-none">
                        <CompassRose size={96} />
                    </div>
                </>
            )}

            {variant === "about" && (
                <>
                    <motion.div
                        style={{ x: pX, y: pY }}
                        className="absolute -top-10 -left-10 w-[520px] h-[320px] pointer-events-none opacity-40"
                    >
                        <SubdivisionGrid className="w-full h-full" opacity={0.5} />
                    </motion.div>
                    <motion.div
                        style={{ x: pX2, y: pY2 }}
                        className="absolute bottom-6 right-14 hidden lg:block pointer-events-none"
                    >
                        <Monument size={80} />
                    </motion.div>
                    <motion.div
                        style={{ x: pX2, y: pY2 }}
                        className="absolute -bottom-20 -right-20 w-[460px] h-[460px] pointer-events-none"
                    >
                        <ContourLines className="w-full h-full" opacity={0.22} count={6} />
                    </motion.div>
                    <FloatingAnnotations />
                </>
            )}

            {variant === "contact" && (
                <>
                    <motion.div
                        style={{ x: pX2, y: pY2 }}
                        className="absolute inset-0 pointer-events-none"
                    >
                        <CoordinateGrid className="absolute inset-0 w-full h-full" opacity={0.25} />
                    </motion.div>
                    <motion.div
                        style={{ x: pX, y: pY }}
                        className="absolute top-14 right-10 hidden lg:block pointer-events-none"
                    >
                        <Monument size={72} />
                    </motion.div>
                    <svg className="absolute bottom-8 left-12 w-[280px] h-[140px] pointer-events-none opacity-80" viewBox="0 0 280 140">
                        <SurveyPoint x="40" y="60" type="BM" label="BM 1248.75" delay={0.2} />
                        <SurveyPoint x="130" y="80" type="IP" label="IP-2" delay={0.5} />
                        <SurveyPoint x="220" y="50" type="MON" label="MON-3" delay={0.8} />
                    </svg>
                </>
            )}

            {variant === "get-started" && (
                <>
                    <motion.div
                        style={{ x: pX, y: pY }}
                        className="absolute -top-20 -right-20 w-[560px] h-[560px] pointer-events-none"
                    >
                        <ContourLines className="w-full h-full" opacity={0.3} count={10} />
                    </motion.div>
                    <div className="absolute top-12 right-8 hidden lg:block pointer-events-none">
                        <CompassRose size={110} />
                    </div>
                    <FloatingAnnotations />
                </>
            )}

            {variant === "blog" && (
                <>
                    <motion.div
                        style={{ x: pX2, y: pY2 }}
                        className="absolute inset-0 pointer-events-none"
                    >
                        <CoordinateGrid className="absolute inset-0 w-full h-full" opacity={0.22} />
                    </motion.div>
                    <FloatingAnnotations />
                </>
            )}

            {variant === "service-detail" && (
                <>
                    <motion.div
                        style={{ x: pX, y: pY }}
                        className="absolute -top-16 -right-20 w-[480px] h-[480px] pointer-events-none"
                    >
                        <ContourLines className="w-full h-full" opacity={0.25} count={8} />
                    </motion.div>
                    <div className="absolute top-10 right-10 hidden lg:block pointer-events-none">
                        <CompassRose size={84} />
                    </div>
                </>
            )}

            {variant === "home" && (
                <>
                    <motion.div
                        style={{ x: pX2, y: pY2 }}
                        className="absolute inset-0 pointer-events-none"
                    >
                        <SurveyingScene className="absolute inset-0 w-full h-full" opacity={0.38} />
                    </motion.div>
                    <motion.div
                        style={{ x: pX, y: pY }}
                        className="absolute -top-10 -right-20 w-[640px] h-[640px] pointer-events-none"
                    >
                        <ContourLines className="w-full h-full" opacity={0.3} count={9} />
                    </motion.div>
                    <motion.div
                        style={{ x: pX, y: pY }}
                        className="absolute -bottom-16 -left-16 w-[420px] h-[260px] pointer-events-none opacity-30"
                    >
                        <SubdivisionGrid className="w-full h-full" opacity={0.45} />
                    </motion.div>
                    <FloatingAnnotations />
                    <motion.div
                        style={{ x: pX, y: pY }}
                        className="absolute top-24 right-10 hidden lg:block pointer-events-none"
                    >
                        <CompassRose size={110} />
                    </motion.div>
                </>
            )}

            {/* Darken overlay for readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#030712]/40 to-[#030712]/80 pointer-events-none" />

            <div className="relative">{children}</div>
        </section>
    );
}
